import React from 'react';
import { useState } from 'react';
import { Folder, FolderOpen, MoreVertical, Star, Share2, Download, Users, Briefcase } from 'lucide-react';
import { FileItem } from '../types';
import { formatDate } from '../utils/fileUtils';
import { ContextMenu } from './ContextMenu';

interface FolderItemProps {
  folder: FileItem;
  viewMode: 'grid' | 'list';
  isTrashView?: boolean;
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onOpen: (folderId: string) => void;
  onDelete: (id: string) => void;
  onRestore?: (id: string) => void;
  onPermanentDelete?: (id: string) => void;
  onToggleStar: (id: string) => void;
  onShare: (id: string) => void;
  onDuplicate: (id: string) => void;
  onMoveTo: (id: string) => void;
  onRename: (id: string) => void;
  onDetails?: (folder: FileItem) => void;
  isGlobalDarkMode?: boolean;
  currentUserName?: string;
  currentUserAvatarUrl?: string | null;
  currentUserId?: string;
}

export const FolderItem: React.FC<FolderItemProps> = ({
  folder,
  viewMode,
  isTrashView = false,
  selectionMode = false,
  isSelected = false,
  onSelect,
  onOpen,
  onDelete,
  onRestore,
  onPermanentDelete,
  onToggleStar,
  onShare,
  onDuplicate,
  onMoveTo,
  onRename,
  onDetails,
  isGlobalDarkMode = false,
  currentUserName,
  currentUserAvatarUrl,
  currentUserId
}) => {
  const isProtected = Boolean(folder.isProtected || folder.isDefault || (folder.name?.toLowerCase() === 'projects' && !folder.parentId));

  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    position: { x: number; y: number };
  }>({ isOpen: false, position: { x: 0, y: 0 } });

  const handleKebabClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      position: { x: rect.right - 200, y: rect.bottom + 5 }
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (isProtected) return;
    onSelect?.(folder.id, !isSelected);
  };

  const handleFolderClick = (e: React.MouseEvent) => {
    if (contextMenu.isOpen) {
      return;
    }

    // Check if the click target is an interactive element (checkbox, button, etc.)
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) {
      return;
    }

    if (selectionMode && onSelect && !isProtected) {
      onSelect(folder.id, !isSelected);
      return;
    }

    onOpen(folder.id);
  };

  const handleContextMenuClose = () => {
    setContextMenu({ ...contextMenu, isOpen: false });
  };

  // Distinct palette design for the default Projects navigation folder (inspired by design reference)
  if (isProtected) {
    if (viewMode === 'grid') {
      return (
        <div
          className="relative group cursor-pointer"
          onClick={handleFolderClick}
        >
          <div
            className={`bg-card rounded-2xl border border-purple-200/70 dark:border-purple-900/50 hover:border-purple-400 dark:hover:border-purple-500 hover:shadow-xl hover:shadow-purple-500/10 transition-all duration-300 aspect-square flex flex-col items-center justify-center p-6 bg-gradient-to-b from-purple-50/40 via-transparent to-transparent dark:from-purple-950/25 dark:via-transparent dark:to-transparent ${
              isSelected ? 'ring-2 ring-purple-500' : ''
            }`}
          >
            {/* 3D Purple Gradient Folder Icon */}
            <div className="relative flex flex-col items-center justify-center mb-3 group-hover:scale-105 transition-transform duration-300">
              {/* Top Folder Tab */}
              <div className="w-10 h-2 bg-gradient-to-r from-fuchsia-400 to-purple-500 rounded-t-md -mb-0.5 shadow-sm opacity-90" />

              {/* Folder Body with Briefcase Icon */}
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 via-purple-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Briefcase className="w-7 h-7 text-white stroke-[2]" />
              </div>
            </div>

            {/* Folder Name */}
            <div className="text-center mt-1">
              <h3 className="text-sm font-semibold text-foreground group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
                {folder.name}
              </h3>
            </div>
          </div>
        </div>
      );
    }

    // List view for Projects default folder
    return (
      <div
        className={`grid grid-cols-12 gap-4 px-6 py-3.5 hover:bg-purple-50/50 dark:hover:bg-purple-950/20 border-border border-b transition-colors duration-200 group cursor-pointer ${
          isSelected ? 'bg-purple-50/80 dark:bg-purple-950/40' : ''
        }`}
        onClick={handleFolderClick}
      >
        <div className="col-span-4 flex items-center gap-3 min-w-0">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm shadow-purple-500/20 flex-shrink-0">
            <Briefcase size={15} className="text-white stroke-[2]" />
          </div>

          <span className="text-sm font-semibold text-foreground truncate">{folder.name}</span>
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/60 dark:text-purple-300">
            Default
          </span>
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">System</span>
        </div>

        <div className="col-span-3 text-sm text-muted-foreground">
          {formatDate(folder.modifiedAt)}
        </div>

        <div className="col-span-2 text-sm text-muted-foreground">
          —
        </div>

        <div className="col-span-1 flex items-center justify-end"></div>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div
        className={`grid grid-cols-12 gap-4 px-6 py-3 hover:bg-accent border-border border-b transition-colors duration-200 group cursor-pointer ${isSelected ? 'bg-workspace-accent/10' : ''}`}
        onClick={handleFolderClick}
      >
        <div className="col-span-4 flex items-center gap-3 min-w-0">
          {onSelect && selectionMode && (
            <input
              type="checkbox"
              checked={isSelected}
              disabled={isProtected}
              onChange={handleCheckboxChange}
              className={`w-4 h-4 text-workspace-accent border-border rounded focus:ring-workspace-accent ${isProtected ? 'opacity-30 cursor-not-allowed' : ''}`}
            />
          )}

          <Folder size={16} className="text-workspace-accent flex-shrink-0" />

          <span className="text-sm text-foreground truncate">{folder.name}</span>
          {isProtected && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
              Default
            </span>
          )}
          {folder.shared && (
            <span title="Shared" className="flex-shrink-0">
              <Users size={12} className="text-workspace-accent" />
            </span>
          )}
        </div>

        <div className="col-span-2 flex items-center gap-2">
          <div className="w-6 h-6 bg-workspace-accent rounded-full flex items-center justify-center overflow-hidden">
            {(folder.ownerAvatarUrl || (folder.ownerId === currentUserId && currentUserAvatarUrl)) ? (
              <img
                src={folder.ownerAvatarUrl || currentUserAvatarUrl || ''}
                alt="Owner"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-white text-xs font-medium">
                {(folder.ownerName || currentUserName || 'User')
                  .split(' ')
                  .map(n => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </span>
            )}
          </div>
          <span className="text-sm text-muted-foreground">{folder.ownerName || currentUserName || 'User'}</span>
        </div>

        <div className="col-span-3 text-sm text-muted-foreground">
          {formatDate(folder.modifiedAt)} {folder.modifiedBy || folder.ownerName || currentUserName || 'User'}
        </div>

        <div className="col-span-2 text-sm text-muted-foreground">
          —
        </div>

        <div className="col-span-1 flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleStar(folder.id);
            }}
            className={`p-1.5 rounded hover:bg-accent transition-colors duration-200 ${folder.starred ? 'text-yellow-500' : 'text-muted-foreground'}`}
          >
            <Star size={14} fill={folder.starred ? 'currentColor' : 'none'} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('Download folder', folder.id);
            }}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors duration-200"
          >
            <Download size={14} />
          </button>

          <button
            onClick={handleKebabClick}
            className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-accent rounded transition-colors duration-200"
          >
            <MoreVertical size={14} />
          </button>
        </div>

        <ContextMenu
          isOpen={contextMenu.isOpen}
          onClose={handleContextMenuClose}
          position={contextMenu.position}
          isFolder={true}
          isTrashView={isTrashView}
          isStarred={folder.starred}
          isShared={folder.shared}
          isProtected={isProtected}
          onShare={() => onShare(folder.id)}
          onDownload={() => console.log('Download folder', folder.id)}
          onRename={() => onRename(folder.id)}
          onToggleStar={() => onToggleStar(folder.id)}
          onDuplicate={() => onDuplicate(folder.id)}
          onMoveTo={() => onMoveTo(folder.id)}
          onDelete={() => onDelete(folder.id)}
          onRestore={() => onRestore?.(folder.id)}
          onPermanentDelete={() => onPermanentDelete?.(folder.id)}
          onOpen={() => onOpen(folder.id)}
          onDetails={() => onDetails?.(folder)}
          isGlobalDarkMode={isGlobalDarkMode}
        />
      </div>
    );
  }

  return (
    <div
      className="relative group cursor-pointer"
      onClick={handleFolderClick}
    >
      <div className={`bg-card rounded-lg border hover:border-border/80 hover:shadow-md transition-all duration-200 aspect-square flex flex-col overflow-hidden ${isSelected ? 'border-workspace-accent ring-1 ring-workspace-accent/30' : 'border-border'}`}>
        {onSelect && selectionMode && (
          <div className="absolute top-2 left-2 z-10">
            <input
              type="checkbox"
              checked={isSelected}
              disabled={isProtected}
              onChange={handleCheckboxChange}
              className={`w-4 h-4 text-workspace-accent border-border rounded focus:ring-workspace-accent ${isProtected ? 'opacity-30 cursor-not-allowed' : ''}`}
            />
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5">
          {isProtected && !selectionMode && (
            <div className="flex items-center gap-1 bg-muted/90 text-foreground text-[10px] font-medium px-1.5 py-0.5 rounded shadow">
              <span>Default</span>
            </div>
          )}
          {folder.shared && !selectionMode && (
            <div className="flex items-center gap-1 bg-workspace-accent/90 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow">
              <Users size={9} />
              <span>Shared</span>
            </div>
          )}
        </div>

        <div className="flex-1 p-6 flex items-center justify-center bg-workspace-accent/10 rounded-t-lg">
          <div className="relative">
            <Folder size={64} className="text-workspace-accent group-hover:text-workspace-accent-hover group-hover:opacity-0 transition-all duration-200" />
            <FolderOpen size={64} className="text-workspace-accent group-hover:text-workspace-accent-hover absolute top-0 left-0 opacity-0 group-hover:opacity-100 transition-all duration-200" />
          </div>
        </div>

        {/* Top right buttons - Share, Download and Kebab menu */}
        <div className="absolute top-2 right-2 z-10 hidden group-hover:flex items-center gap-1 transition-all duration-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onShare(folder.id);
            }}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <Share2 size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('Download folder', folder.id);
            }}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <Download size={14} />
          </button>
          <button
            onClick={handleKebabClick}
            className="p-1.5 bg-card/90 hover:bg-card text-foreground rounded transition-colors duration-200 shadow-sm border border-border/50"
          >
            <MoreVertical size={14} />
          </button>
        </div>

        <div className="p-4 flex-shrink-0">
          <div className="flex items-center justify-between text-muted-foreground">
            <h3 className="text-sm font-medium text-foreground truncate">{folder.name}</h3>
            <span className="text-xs">{formatDate(folder.modifiedAt)}</span>
          </div>
        </div>
      </div>

      <ContextMenu
        isOpen={contextMenu.isOpen}
        onClose={handleContextMenuClose}
        position={contextMenu.position}
        isFolder={true}
        isTrashView={isTrashView}
        isStarred={folder.starred}
        isShared={folder.shared}
        isProtected={isProtected}
        onShare={() => onShare(folder.id)}
        onDownload={() => console.log('Download folder', folder.id)}
        onRename={() => onRename(folder.id)}
        onToggleStar={() => onToggleStar(folder.id)}
        onDuplicate={() => onDuplicate(folder.id)}
        onMoveTo={() => onMoveTo(folder.id)}
        onDelete={() => onDelete(folder.id)}
        onRestore={() => onRestore?.(folder.id)}
        onPermanentDelete={() => onPermanentDelete?.(folder.id)}
        onOpen={() => onOpen(folder.id)}
        onDetails={() => onDetails?.(folder)}
        isGlobalDarkMode={isGlobalDarkMode}
      />
    </div>
  );
};

